/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */

import { DestroyRef, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Map as OLMap, MapBrowserEvent } from 'ol';
import { Coordinate, rotate as rotateCoordinate, scale as scaleCoordinate } from 'ol/coordinate';
import { all, Condition, focusWithTabindex, noModifierKeys, primaryAction } from 'ol/events/condition';
import Event from 'ol/events/Event';
import Feature from 'ol/Feature';
import { LineString, Polygon } from 'ol/geom';
import { DEVICE_PIXEL_RATIO } from 'ol/has';
import PointerInteraction, { centroid as centroidFromPointers } from 'ol/interaction/Pointer';
import VectorLayer from 'ol/layer/Vector';
import { METERS_PER_UNIT, transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { getArea, getDistance } from 'ol/sphere';
import Style from 'ol/style/Style';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ThemeState, ThemeVariant } from '../../../core/ui/theme-switcher/state/theme.state';
import { MeasureEventType, MeasureOptions, MeasureUnit } from '../../../types/measure.types';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { MeasurementValue, MeasurementValues } from '../command/measure/measurementValue.class';

export class MeasureEvent extends Event {
    measurement: MeasurementValues;
    altMeasurement: MeasurementValues;

    constructor(type: string, measurement?: MeasurementValues, altMeasurement?: MeasurementValues) {
        super(type);

        this.measurement = measurement;
        this.altMeasurement = altMeasurement;
    }
}

export class Measure extends PointerInteraction {
    squaredClickTolerance = 100;
    squaredSnapTolerance = 100;

    private measuring = false;
    private nearPoint: number = null;
    private pointBeingModded: number = null; // index of point being moved or deleted
    private readonly snapClosed = true;
    private displayUnits: MeasureUnit = null;
    private condition: Condition = all(primaryAction, focusWithTabindex, noModifierKeys);

    private lastDownPixel: number[] = null;
    private lastPointerCount: number;
    private points: Coordinate[] = [];
    private segments: number[] = [];
    private measurement: MeasurementValues = {
        distance: new MeasurementValue(0),
    };

    private panning = false;
    private lastCentroid: Coordinate = null;

    private overlay: VectorLayer<VectorSource>;

    private colorScheme: ThemeVariant;
    private store: Store;

    private measureStop = new Subject<void>();

    constructor(options: MeasureOptions) {
        super(options);

        if (options.snapClosed) {
            this.snapClosed = options.snapClosed;
        }

        if (options.displayUnits) {
            this.displayUnits = options.displayUnits;
        }

        this.overlay = new VectorLayer({
            source: new VectorSource({
                useSpatialIndex: false,
                wrapX: false,
            }),
            style: options.style ? options.style : this.getDefaultStyleFunction(),
            updateWhileInteracting: true,
        });
        this.store = options.store;
    }

    startMeasure(evt: MapBrowserEvent<UIEvent>) {
        this.store.select(ThemeState.variant).pipe(
            takeUntil(this.measureStop),
        ).subscribe(lightDark => {
            this.colorScheme = lightDark;
            if (this.measuring) {
                this.updateSource();
            }
        });
        this.points = [evt.coordinate];
        this.measuring = true;
        this.dispatchEvent(new MeasureEvent(MeasureEventType.START));
        this.updateSource();
    }

    setMap(map) {
        super.setMap(map);
        if (!map) {
            // this.clearMeasure();
        }
        if (!this.displayUnits) {
            this.displayUnits = map.getView().getProjection().getUnits();
        }
        this.updateState();
    }

    clearMeasure() {
        this.points = [];

        const source = this.overlay.getSource();
        if (source) {
            source.clear(true);
        }
        this.overlay.setMap(null);

        this.dispatchEvent(new MeasureEvent(MeasureEventType.CLEAR, null));

        this.endMeasure();
    }

    protected handleDownEvent(evt: MapBrowserEvent<any>): boolean {
        if (this.targetPointers.length !== 1 || !this.condition(evt)) {
            return false;
        }
        if (this.nearPoint >= 0) {
            this.lastDownPixel = evt.pixel;
            this.pointBeingModded = this.nearPoint;
        } else {
            // this is a touch device, so we search for the nearest point within tolerance
            const nearPoint = this.getNearestPoint(evt.pixel, this.squaredClickTolerance);
            if (nearPoint) {
                this.lastDownPixel = evt.pixel;
                this.pointBeingModded = nearPoint;
            }
        }
        if (this.targetPointers.length > 0 && this.condition(evt)) {
            const view = evt?.map?.getView();
            this.lastCentroid = null;

            // stop any current animation
            if (view?.getAnimating()) {
                view.cancelAnimations();
            }
        }
        return true;
    }

    /**
     *
     * @param evt {MapBrowserEvent}
     * @protected
     *
     * This handler needs to differentiate between different user actions:
     * 1. Click or tap with a small amount of movement
     *    Simple to check against `squaredClickTolerance` and ignore if less
     * 2. Drag when down action was near a point
     *    In this case we indicate that we are moving a point
     * 3. Drag otherwise
     *    In this case, we are panning the map.
     */
    protected handleDragEvent(evt: MapBrowserEvent<any>): void {
        if (this.pointBeingModded !== null) {
            this.points[this.pointBeingModded] = evt.coordinate;
            this.updateSource();
            return;
        }

        const map = evt.map;
        const view = map?.getView();

        if (!this.panning) {
            this.panning = true;
            view.beginInteraction();
        }
        const targetPointers = this.targetPointers;
        const centroid = map.getEventPixel(centroidFromPointers(targetPointers));
        if (targetPointers.length === this.lastPointerCount) {
            if (this.lastCentroid) {
                const delta = [this.lastCentroid[0] - centroid[0], centroid[1] - this.lastCentroid[1]];
                scaleCoordinate(delta, view.getResolution());
                rotateCoordinate(delta, view.getRotation());
                view.adjustCenterInternal(delta);
            }
        }
        this.lastCentroid = centroid;
        this.lastPointerCount = targetPointers.length;
    }

    protected handleUpEvent(evt: MapBrowserEvent<MouseEvent>): boolean {
        if (this.targetPointers.length !== 0) {
            return false;
        }

        if (this.pointBeingModded !== null && (this.pointBeingModded > 0 || this.points.length < 3)) {
            if (!this.isDragging(evt)) {
                this.points.splice(this.pointBeingModded, 1);
            }
            this.pointBeingModded = null;
            this.updateSource();
            return true;
        }

        if (this.panning) {
            const view = evt.map?.getView();
            if (view) {
                view.endInteraction();
            }
            this.panning = false;
            return false;
        }

        if (this.measuring) {
            let measurePoint = evt.coordinate;
            if (
                this.snapClosed &&
                this.getSquaredDistance(evt.pixel, this.getMap().getPixelFromCoordinate(this.points[0])) <
                    this.squaredSnapTolerance
            ) {
                measurePoint = DashGisUtil.clone(this.points[0]);
            }
            this.points.push(measurePoint);

            this.pointBeingModded = null;
            this.updateSource();
            return true;
        }

        this.startMeasure(evt);
        return true;
    }

    protected handleMoveEvent(evt: MapBrowserEvent<any>): void {
        this.nearPoint = this.getNearestPoint(evt.pixel, this.squaredClickTolerance);
    }

    private isDragging(evt: MapBrowserEvent<any>): boolean {
        return (
            this.lastDownPixel && this.getSquaredDistance(evt.pixel, this.lastDownPixel) > this.squaredClickTolerance
        );
    }

    /**
     * Finds the nearest point in the array `this.points` within the given tolerance
     * @param pixel {number[]}
     * @param tolerance {number}
     * @return {number|null} the index of the nearest point
     * @private
     */
    private getNearestPoint(pixel: number[], tolerance: number): number {
        let foundNearest = null;
        let nearestPointSquaredDistance = Number.MAX_VALUE;
        this.points.forEach((point, i) => {
            const testPixel = this.getPixel(this.getMap(), point);
            const d = this.getSquaredDistance(testPixel, pixel);
            if (d < tolerance) {
                if (!foundNearest || d < nearestPointSquaredDistance) {
                    foundNearest = i;
                    nearestPointSquaredDistance = d;
                }
            }
        });
        return foundNearest;
    }

    private getSquaredDistance(a: number[], b: number[]) {
        return Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2);
    }

    private getPixel(map: OLMap, coordinate: Coordinate) {
        return map.getPixelFromCoordinate(coordinate);
    }

    private isClosedPath(): boolean {
        let result = false;
        if (this.points.length > 2) {
            if (
                this.getSquaredDistance(this.points[0], this.points[this.points.length - 1]) < this.squaredSnapTolerance
            ) {
                result = true;
            }
        }

        return result;
    }

    private updateState() {
        const map = this.getMap();
        const active = this.getActive();

        if (active) {
            if (map && !this.overlay.getMapInternal()) {
                this.overlay.setMap(map);
            }
        } else {
            this.overlay.setMap(null);
        }
    }

    private updateSource() {
        const overlaySource = this.overlay.getSource();
        overlaySource.clear(true);
        const rawMeasurement: MeasurementValues = {
            distance: new MeasurementValue(0),
            area: null,
        };
        if (this.points.length > 0) {
            const units = this.getMapUnits();
            const geometry = new LineString(this.points);
            const lineFeature = new Feature(geometry);
            this.segments = [];
            rawMeasurement.distance.value = this.points.reduce((distance, point, i, pointArray) => {
                if (i > 0) {
                    const segmentLength = this.measureLine(point, pointArray[i - 1]);
                    this.segments.push(segmentLength);
                    return distance + segmentLength;
                }
                return distance;
            }, 0 as number);
            rawMeasurement.distance.units = units as MeasureUnit;
            overlaySource.addFeatures([lineFeature]);
            if (this.snapClosed && this.isClosedPath()) {
                rawMeasurement.area = this.convertUnits(
                    new MeasurementValue(this.measureArea(this.points), `sqm` as MeasureUnit),
                    `sq${units}` as MeasureUnit
                );
            } else {
                rawMeasurement.area = null;
            }
        } else {
            rawMeasurement.distance.value = 0;
            rawMeasurement.area = null;
            this.measuring = false;
        }

        const displayMeasurement = DashGisUtil.clone(rawMeasurement);
        const altDisplayMeasurement = DashGisUtil.clone(rawMeasurement);

        displayMeasurement.distance = this.convertUnits(rawMeasurement.distance, this.displayUnits);
        altDisplayMeasurement.distance = this.convertUnits(
            displayMeasurement.distance,
            this.getAlternateUnits(this.displayUnits)
        );

        displayMeasurement.distance = this.scaleUnits(displayMeasurement.distance);
        altDisplayMeasurement.distance = this.scaleUnits(altDisplayMeasurement.distance);

        if (rawMeasurement?.area?.value) {
            const areaDisplayUnits = `sq${this.displayUnits}` as MeasureUnit;
            displayMeasurement.area = this.convertUnits(rawMeasurement.area, areaDisplayUnits);
            altDisplayMeasurement.area = this.convertUnits(
                displayMeasurement.area,
                this.getAlternateUnits(areaDisplayUnits)
            );

            displayMeasurement.area = this.scaleUnits(displayMeasurement.area);
            altDisplayMeasurement.area = this.scaleUnits(altDisplayMeasurement.area);
        }

        this.measurement = displayMeasurement;

        this.dispatchEvent(new MeasureEvent(MeasureEventType.CHANGE, displayMeasurement, altDisplayMeasurement));
    }

    private getMapUnits() {
        return this.getMap().getView().getProjection().getUnits();
    }

    private getAlternateUnits(units: MeasureUnit) {
        switch (units) {
            case 'm':
                return 'ft';
            case 'km':
                return 'mi';
            case 'sqm':
                return 'sqft';
            case 'sqkm':
                return 'sqmi';
            case 'ft':
                return 'm';
            case 'mi':
                return 'km';
            case 'sqft':
                return 'acre';
            case 'sqmi':
                return 'acre';
        }
    }

    private convertUnits(source: MeasurementValue, targetUnits: MeasureUnit): MeasurementValue {
        return new MeasurementValue(source.value * this.getConversionFactor(source.units, targetUnits), targetUnits);
    }

    private getConversionFactor(sourceUnits: string, targetUnits: string) {
        if (sourceUnits === targetUnits) {
            return 1.0;
        }
        const FEET_PER_METER = 1 / METERS_PER_UNIT['ft'];
        const FEET_PER_METER_SQUARED = 1 / Math.pow(METERS_PER_UNIT['ft'], 2);
        const FEET_PER_MILE = 5280.0;
        const FEET_PER_MILE_SQUARED = 27878400.0;
        const SQUARE_FEET_PER_ACRE = 43560;
        const SQUARE_METER_PER_ACRE = 4046.856;
        const conversions = {
            m: {
                ft: FEET_PER_METER,
                mi: FEET_PER_METER / FEET_PER_MILE,
                km: 0.001,
            },
            ft: {
                m: METERS_PER_UNIT.ft,
                km: 0.001 / METERS_PER_UNIT.ft,
                mi: 1 / 5280.0,
            },
            sqm: {
                sqft: FEET_PER_METER_SQUARED,
                sqmi: FEET_PER_METER_SQUARED / FEET_PER_MILE_SQUARED,
                sqkm: 0.000001,
                acre: 1 / SQUARE_METER_PER_ACRE,
            },
            sqft: {
                sqm: 1 / FEET_PER_METER_SQUARED,
                sqkm: 1 / FEET_PER_METER_SQUARED / 1000000,
                sqmi: 1 / FEET_PER_MILE_SQUARED,
                acre: 1 / SQUARE_FEET_PER_ACRE,
            },
        };
        const sourceConversion = conversions[sourceUnits];
        if (sourceConversion) {
            const targetConversion = sourceConversion[targetUnits];
            if (targetConversion) {
                return targetConversion;
            }
        }
        throw Error(`Conversion between ${sourceUnits} and ${targetUnits} has not been configured`);
    }

    /**
     * Scale a measurement unit to a larger scale unit of the same type when it gets too large. For example,
     * 15000 ft would scale to 2.84 mi
     * @param {MeasurementValue} measurement
     * @return {string}
     * @private
     */
    private scaleUnits(measurement: MeasurementValue): MeasurementValue {
        let result = new MeasurementValue(measurement.value, measurement.units);

        switch (measurement.units) {
            case 'm':
                if (measurement.value > 1000) {
                    result = this.convertUnits(measurement, 'km');
                }
                break;
            case 'sqm':
                if (measurement.value > 1000000) {
                    result = this.convertUnits(measurement, 'sqkm');
                }
                break;
            case 'ft':
                if (measurement.value > 5280) {
                    result = this.convertUnits(measurement, 'mi');
                }
                break;
            case 'sqft':
                if (measurement.value > 27878400) {
                    result = this.convertUnits(measurement, 'sqmi');
                }
                break;
        }
        return result;
    }

    private getAngle(a: number[], b: number[], forText = false): number {
        const actualAngle = Math.atan2(b[1] - a[1], b[0] - a[0]);
        if (forText) {
            return Math.abs(actualAngle) > Math.PI / 2 ? actualAngle - Math.PI : actualAngle;
        }
        return actualAngle;
    }

    private getDivisionScale(resolution: number): number {
        const target = 200 / DEVICE_PIXEL_RATIO;
        const forwardFactors = [1, 2, 5];
        const reverseFactors = [5, 2, 1];
        let ppd = 1 / resolution;
        let exp = 0;
        let previousScale;
        let scale = 1;
        let direction;
        let factors;
        let delta, previousDelta;
        while (true) {
            if (ppd < target) {
                direction = 1;
                factors = forwardFactors;
            } else {
                direction = -1;
                factors = reverseFactors;
            }
            for (const factor of factors) {
                scale = factor * Math.pow(10, exp);
                ppd = scale / resolution;
                delta = Math.abs(target - ppd);
                if (previousDelta && previousDelta < delta) {
                    return previousScale;
                }
                previousDelta = delta;
                previousScale = scale;
            }
            exp += direction;
        }
    }

    private isDivision(value, divisionScale) {
        const normalizer = divisionScale < 1 ? 1 / divisionScale : 1;
        const normalizedValue = normalizer * value;
        const normalizedScale = normalizer * divisionScale;
        return normalizedValue % normalizedScale < 1e-8;
    }

    private measureLine(c1, c2) {
        const projection = this.getMap().getView().getProjection();
        // correct for errors in Spherical Mercator
        const t1 = transform(c1, projection, 'EPSG:4326');
        const t2 = transform(c2, projection, 'EPSG:4326');

        // get distance on sphere
        return getDistance(t1, t2);
    }

    private measureArea(input: Coordinate[]) {
        const projection = this.getMap().getView().getProjection();
        const coordinates = [];
        input.forEach((c) => {
            coordinates.push(transform(c, projection, 'EPSG:4326'));
        });
        // Polygon constructor takes an array of coordinate arrays, each being a linear ring
        const geom = new Polygon([coordinates]);
        return Math.abs(getArea(geom, { projection: 'EPSG:4326' }));
    }

    private endMeasure() {
        this.measureStop.next();
        this.measuring = false;
        this.dispatchEvent(new MeasureEvent(MeasureEventType.END, null));
    }

    private getDefaultStyleFunction() {
        let style = null;
        return () => {
            if (!style) {
                style = this.createMeasureStyle();
            }
            return style;
        };
    }

    private createMeasureStyle() {
        const nodeSize = 6.0;

        const map = this.getMap();
        const proj = map.getView().getProjection();
        const mapUnits = proj.getUnits();

        const drawLine = (ctx: CanvasRenderingContext2D, start: number[], end: number[]) => {
            ctx.beginPath();
            ctx.strokeStyle = this.colorScheme === 'light' ? 'black' : 'white';
            ctx.lineWidth = 2.5;
            ctx.lineJoin = 'round';
            ctx.moveTo(start[0], start[1]);
            ctx.lineTo(end[0], end[1]);
            ctx.stroke();
        };

        const drawHash = (
            ctx: CanvasRenderingContext2D,
            start: number[],
            angle: number,
            size: number,
            text?: string
        ) => {
            ctx.beginPath();
            ctx.strokeStyle = this.colorScheme === 'light' ? 'black' : 'white';
            ctx.lineWidth = 1.5;
            ctx.moveTo(start[0], start[1]);
            ctx.save();
            ctx.translate(start[0], start[1]);
            ctx.rotate(-angle);
            ctx.lineTo(0, -size);
            ctx.stroke();
            if (text) {
                ctx.font = 'bold 10pt Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = this.colorScheme === 'light' ? 'black' : 'white';
                if (Math.abs(angle) > Math.PI / 2) {
                    ctx.rotate(-Math.PI);
                    ctx.fillText(text, 0, size + 8);
                } else {
                    ctx.fillText(text, 0, -(size + 8));
                }
            }
            ctx.restore();
        };

        // construct node image
        const drawNode = (ctx: CanvasRenderingContext2D, point: number[], label: string, labelAngle: number) => {
            const x = point[0];
            const y = point[1];
            ctx.beginPath();
            ctx.moveTo(x, y);
            const p = new Path2D();
            p.arc(x, y, nodeSize * 1.5, 0, 2 * Math.PI);
            ctx.fillStyle = this.colorScheme === 'light' ? 'white' : 'black';
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = 'rgb(0, 0, 0, 0)';
            ctx.fill(p);
            ctx.beginPath();
            ctx.strokeStyle = this.colorScheme === 'light' ? 'black' : 'white';
            ctx.arc(x, y, nodeSize, 0, 2.0 * Math.PI);
            ctx.stroke();
            ctx.moveTo(x, y);
            if (label) {
                ctx.save();
                ctx.beginPath();
                ctx.translate(x, y);
                ctx.font = 'bold 11pt Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = this.colorScheme === 'light' ? 'black' : 'white';
                ctx.rotate(labelAngle);
                ctx.fillText(label, 0, 24);
                ctx.restore();
            }
        };

        const drawHashes = (pairs: Coordinate[], ctx: CanvasRenderingContext2D) => {
            // draw hashes
            // need to find pixels per division and keep it to around 200-400(?)
            const conversion = this.getConversionFactor(mapUnits, this.measurement.distance.units);
            const resolution = map.getView().getResolution();
            const majorDivisionScale = this.getDivisionScale(conversion * resolution);
            const minorDivisionScale = majorDivisionScale / 2;
            const divisionSubscale = majorDivisionScale / 10;
            let cumulativeDistance = 0;
            pairs.forEach((coordinate, i) => {
                if (i <= 0) {
                    return;
                }
                const startCoordinate = pairs[i - 1];
                const mapSegment = new MeasurementValue(this.segments[i - 1], mapUnits as MeasureUnit);
                const segmentDistance = this.convertUnits(mapSegment, this.measurement.distance.units);
                // loop over hashes
                let offset = divisionSubscale - (cumulativeDistance % divisionSubscale);
                const hashDistance = new MeasurementValue(0, this.measurement.distance.units);
                while (offset < segmentDistance.value) {
                    hashDistance.value = cumulativeDistance + offset;
                    const isMajorDivision = this.isDivision(hashDistance.value, majorDivisionScale);
                    const isMinorDivision = this.isDivision(hashDistance.value, minorDivisionScale);
                    const labelText = isMajorDivision ? hashDistance.label : null;
                    const hashSize = 8 + (isMajorDivision ? 4 : 0) + (isMinorDivision ? 4 : 0);
                    const t = offset / segmentDistance.value;
                    const x = (1 - t) * startCoordinate[0] + t * coordinate[0];
                    const y = (1 - t) * startCoordinate[1] + t * coordinate[1];
                    const hashCoords = this.getPixel(map, [x, y]).map((axis) => axis * DEVICE_PIXEL_RATIO);
                    // find nearest multiple of divisionSubScale that is equal or greater than start distance
                    drawHash(ctx, hashCoords, this.getAngle(startCoordinate, coordinate), hashSize, labelText);
                    offset += divisionSubscale;
                }
                cumulativeDistance += segmentDistance.value;
            });
        };

        return new Style({
            renderer: (coords, state) => {
                let lastPixel = null;
                const ctx = state.context;
                if (Array.isArray(coords) && typeof coords[0] === 'number') {
                    return; // is the current mouse location
                }
                // linestring
                // openlayers will simplify the linestring for rendering, removing points that are on a straight line
                // between the surrounding points. So we render using the list of points we already maintain.
                const pairs = [...this.points];
                const pixels = pairs.map((pair) =>
                    map.getPixelFromCoordinate(pair).map((val) => Math.floor(val * DEVICE_PIXEL_RATIO))
                );

                // draw measure lines
                let pixelDistance = 0;
                pixels.forEach((pixel) => {
                    if (lastPixel) {
                        drawLine(ctx, lastPixel, pixel);
                        pixelDistance += Math.sqrt(this.getSquaredDistance(lastPixel, pixel));
                    }
                    lastPixel = pixel;
                });

                drawHashes(pairs, ctx);

                // draw nodes
                pixels.forEach((pixel, i) => {
                    let distanceString = null;
                    let distanceAngle = null;
                    if (i === 0 && this.points.length > 1) {
                        distanceString = '0';
                        distanceAngle = this.getAngle(pixel, pixels[i + 1], true);
                    } else if (i !== 0 && i === this.points.length - 1) {
                        distanceString = this.measurement.distance.label;
                        distanceAngle = this.getAngle(pixels[i - 1], pixel, true);
                    }
                    drawNode(ctx, pixel, distanceString, distanceAngle);
                });
            },
        });
    }
}
